import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import Layout from "@/components/layout/Layout";
/* Eager routes — kept in the initial JS bundle because they are the
   primary landing surfaces (homepage, catalogue, PDPs) and the 404
   fallback (which must be able to render synchronously if the URL
   doesn't match anything). Everything else is code-split via React.lazy
   below so the initial bundle only ships what a first-time visitor
   actually paints. */
import HomePage from "@/pages/HomePage";
import CatalogPage from "@/pages/CatalogPage";
import ProductPage from "@/pages/ProductPage";
import NotFoundPage from "@/pages/NotFoundPage";
// LearnIndexPage is a landing page in its own right, and SiteGuru
// measurably penalised /learn (score 28) when it had to wait for the
// separate `learn` chunk before painting. Keeping it eager removes the
// Suspense fallback flash and improves LCP without materially growing
// the main bundle (the hub only imports lightweight metadata from
// learnGuides.js, no per-guide bodies).
import LearnIndexPage from "@/pages/LearnIndexPage";
// Single source of truth for static-route metadata. This same JSON file is
// read at build time by scripts/generate-og-pages.js so the prerendered
// HTML crawlers see always matches the runtime meta React applies after
// hydration. See test_page_meta_sync.py for the guardrail.
import PAGE_META_JSON from "@/data/pageMeta.json";

/* Lazy routes — grouped by intent so webpack emits a small number of
   named chunks rather than 30+ micro-chunks. The `webpackChunkName`
   magic comment names each chunk in the emitted /static/js/ output. */

// Learn hub + every /learn/* guide land in a single chunk. Visitors
// rarely enter through Learn on their first session; when they do,
// the whole guide library becomes available at once. The hub itself
// (LearnIndexPage) is eager — see import above.
const LearnNadPlusPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnNadPlusPage"));
const LearnRetatrutidePage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnRetatrutidePage"));
const LearnPeptideBlendsPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnPeptideBlendsPage"));
const LearnGhkCuPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnGhkCuPage"));
const LearnBpc157Page = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnBpc157Page"));
const LearnSemaxPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnSemaxPage"));
const LearnKpvPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnKpvPage"));
const LearnSemaxVsSelankPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnSemaxVsSelankPage"));
const LearnCjc1295DacVsNoDacPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnCjc1295DacVsNoDacPage"));
const LearnBpc157VsTb500Page = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnBpc157VsTb500Page"));
const LearnGlowVsKlowPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnGlowVsKlowPage"));
const LearnWhatIsTb500Page = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsTb500Page"));
const LearnWhatIsIpamorelinPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsIpamorelinPage"));
const LearnHowToReadPeptideCoaPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnHowToReadPeptideCoaPage"));
const LearnWhatIsCagrilintidePage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsCagrilintidePage"));
const LearnWhatIsMotsCPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsMotsCPage"));
const LearnMelanotan1Vs2Page = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnMelanotan1Vs2Page"));
const LearnWhatIsEpitalonPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsEpitalonPage"));
const LearnWhatIsDsipPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsDsipPage"));
const LearnWhatIsAod9604Page = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsAod9604Page"));
const LearnWhatIsPt141Page = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsPt141Page"));
const LearnWhatIsCjc1295Page = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsCjc1295Page"));
const LearnWhatIsSelankPage = lazy(() => import(/* webpackChunkName: "learn" */ "@/pages/LearnWhatIsSelankPage"));

// Checkout flow — Cart -> Checkout -> Payment Success. Only loaded once
// a visitor actually starts a purchase.
const CartPage = lazy(() => import(/* webpackChunkName: "checkout" */ "@/pages/CartPage"));
const CheckoutPage = lazy(() => import(/* webpackChunkName: "checkout" */ "@/pages/CheckoutPage"));
const PaymentSuccessPage = lazy(() => import(/* webpackChunkName: "checkout" */ "@/pages/PaymentSuccessPage"));

// Account + auth callback surfaces. Behind sign-in so almost never on
// the critical path for a first-time visitor.
const OrdersPage = lazy(() => import(/* webpackChunkName: "account" */ "@/pages/OrdersPage"));
const AuthCallback = lazy(() => import(/* webpackChunkName: "account" */ "@/pages/AuthCallback"));
const VerifyLinkPage = lazy(() => import(/* webpackChunkName: "account" */ "@/pages/VerifyLinkPage"));

// Admin — private, huge, absolutely no need to ship it to the public.
const AdminPage = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPage"));

// Bundles + secondary catalogue pages.
const BundlePage = lazy(() => import(/* webpackChunkName: "bundles" */ "@/pages/BundlePage"));
const BundlesPage = lazy(() => import(/* webpackChunkName: "bundles" */ "@/pages/BundlesPage"));
const BlendsPage = lazy(() => import(/* webpackChunkName: "bundles" */ "@/pages/BlendsPage"));
const AccessoriesPage = lazy(() => import(/* webpackChunkName: "bundles" */ "@/pages/AccessoriesPage"));

// Trust / information / support pages. Low-traffic relative to /peptides
// and PDPs; grouping them into one chunk keeps their combined transfer
// under a single request when someone actually visits.
const CoaLibraryPage = lazy(() => import(/* webpackChunkName: "info" */ "@/pages/CoaLibraryPage"));
const AboutPage = lazy(() => import(/* webpackChunkName: "info" */ "@/pages/AboutPage"));
const ContactPage = lazy(() => import(/* webpackChunkName: "info" */ "@/pages/ContactPage"));
const VerifyPage = lazy(() => import(/* webpackChunkName: "info" */ "@/pages/VerifyPage"));
const AuthenticityPage = lazy(() => import(/* webpackChunkName: "info" */ "@/pages/AuthenticityPage"));
const CryptoGuidePage = lazy(() => import(/* webpackChunkName: "info" */ "@/pages/CryptoGuidePage"));

/* Fallback while a lazy chunk downloads. Keeps the branded chrome from
   Layout in place (header/footer are eager) so the visual continuity is
   preserved — only the route body swaps. */
function RouteFallback() {
  return <div className="container-pd py-24 text-center text-subtle" data-testid="route-loading">Loading…</div>;
}

const PAGE_META = {
  ...PAGE_META_JSON,
  // Utility / non-indexable routes: these do not go through the prerender
  // step so they live only here in the runtime map. Explicit robots meta
  // prevents SPA nav from ever indexing them by accident.
  "/payment/success": { title: "Payment Confirmed · PepDirect", description: "Payment received. Your PepDirect research peptide order is being processed." },
  "/cart": { title: "Cart · PepDirect", description: "Review your research peptide cart before checkout.", robots: "noindex, follow" },
  "/checkout": { title: "Checkout · PepDirect", description: "Complete your PepDirect research peptide order.", robots: "noindex, follow" },
  "/admin": { title: "Admin · PepDirect", description: "PepDirect admin dashboard.", robots: "noindex, nofollow" },
  "/auth/verify": { title: "Sign In · PepDirect", description: "Sign in to PepDirect.", robots: "noindex, follow" },
};

const PAGE_TITLES = Object.fromEntries(
  Object.entries(PAGE_META).map(([k, v]) => [k, v.title])
);

// Sets title, description, and canonical for every route. Product & bundle
// detail pages own their own metadata via useSeo/ProductJsonLd — this hook
// intentionally skips them so per-item titles win and don't get clobbered.
function RouteTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Detail pages own their own title/description/canonical via
    // ProductPage/BundlePage. We still eagerly clear any inherited
    // robots meta before returning early so a noindex directive set
    // by a utility page (/cart, /checkout, /admin) doesn't leak into
    // an indexable PDP after SPA navigation.
    const isDetailPage =
      pathname.startsWith("/product/") || pathname.startsWith("/bundle/");
    if (isDetailPage) {
      document.head.querySelector('meta[name="robots"]')?.remove();
      return;
    }

    const meta = PAGE_META[pathname] || PAGE_META["/"];
    document.title = meta.title;

    const setMeta = (name, content) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", meta.description);
    if (meta.robots) setMeta("robots", meta.robots);
    else {
      document.head.querySelector('meta[name="robots"]')?.remove();
    }

    // Update canonical to match the current path on pepdirect.co so PDP
    // canonicals don't linger after client-side navigation.
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://pepdirect.co${pathname === "/" ? "/" : pathname}`);
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function AppRoutes() {
  // Emergent Google Auth returns the user to `<origin>/#session_id=…`.
  // We MUST detect this synchronously (during render, not useEffect)
  // so the callback exchanges the token BEFORE AuthContext.loadUser()
  // fires against `/auth/me` and 401s. `useLocation().hash` is
   // reactive; window.location.hash is not — see playbook.
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    // AuthCallback is lazy-loaded (in the "account" chunk); the Suspense
    // fallback shows a plain "Signing you in…" state while the chunk
    // fetches.
    return (
      <Suspense fallback={<div className="container-pd py-24 text-center text-subtle">Signing you in…</div>}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/auth/verify" element={<VerifyLinkPage />} />
        <Route path="/admin" element={<AdminPage />} />
      <Route
        path="*"
        element={
          <Layout>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/peptides" element={<CatalogPage />} />
                <Route path="/pen-kits" element={<CatalogPage fixedFormat="pen" title="Pen Kits" eyebrow="↳ Ready-to-use pens" intro="Pre-dosed, dial-a-dose pen kits — no reconstitution, no math, no equipment." />} />
                <Route path="/raw-vials" element={<CatalogPage fixedFormat="raw" title="Raw Vials" eyebrow="↳ Lyophilised vials" intro="Lyophilised research peptides at the lowest cost per mg. Full manual dosing control." />} />
                <Route path="/vial-kits" element={<Navigate to="/raw-vials" replace />} />
                <Route path="/product/:slug" element={<ProductPage />} />
                <Route path="/bundle/:slug" element={<BundlePage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/coa" element={<CoaLibraryPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/verify" element={<VerifyPage />} />
                <Route path="/authenticity" element={<AuthenticityPage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/crypto-guide" element={<CryptoGuidePage />} />
                <Route path="/learn" element={<LearnIndexPage />} />
                <Route path="/learn/nad-plus" element={<LearnNadPlusPage />} />
                <Route path="/learn/retatrutide" element={<LearnRetatrutidePage />} />
                <Route path="/learn/peptide-blends" element={<LearnPeptideBlendsPage />} />
                <Route path="/learn/ghk-cu" element={<LearnGhkCuPage />} />
                <Route path="/learn/bpc-157" element={<LearnBpc157Page />} />
                <Route path="/learn/semax" element={<LearnSemaxPage />} />
                <Route path="/learn/kpv" element={<LearnKpvPage />} />
                <Route path="/learn/semax-vs-selank" element={<LearnSemaxVsSelankPage />} />
                <Route path="/learn/cjc-1295-dac-vs-no-dac" element={<LearnCjc1295DacVsNoDacPage />} />
                <Route path="/learn/bpc-157-vs-tb-500" element={<LearnBpc157VsTb500Page />} />
                <Route path="/learn/glow-vs-klow" element={<LearnGlowVsKlowPage />} />
                <Route path="/learn/what-is-tb-500" element={<LearnWhatIsTb500Page />} />
                <Route path="/learn/what-is-ipamorelin" element={<LearnWhatIsIpamorelinPage />} />
                <Route path="/learn/how-to-read-peptide-coa" element={<LearnHowToReadPeptideCoaPage />} />
                <Route path="/learn/what-is-cagrilintide" element={<LearnWhatIsCagrilintidePage />} />
                <Route path="/learn/what-is-mots-c" element={<LearnWhatIsMotsCPage />} />
                <Route path="/learn/melanotan-1-vs-2" element={<LearnMelanotan1Vs2Page />} />
                <Route path="/learn/what-is-epitalon" element={<LearnWhatIsEpitalonPage />} />
                <Route path="/learn/what-is-dsip" element={<LearnWhatIsDsipPage />} />
                <Route path="/learn/what-is-aod-9604" element={<LearnWhatIsAod9604Page />} />
                <Route path="/learn/what-is-pt-141" element={<LearnWhatIsPt141Page />} />
                <Route path="/learn/what-is-cjc-1295" element={<LearnWhatIsCjc1295Page />} />
                <Route path="/learn/what-is-selank" element={<LearnWhatIsSelankPage />} />
                <Route path="/accessories" element={<AccessoriesPage />} />
                <Route path="/blends" element={<BlendsPage />} />
                <Route path="/bundles" element={<BundlesPage />} />
                <Route path="/account/orders" element={<OrdersPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Layout>
        }
      />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrencyProvider>
          <CartProvider>
            <Toaster position="top-center" richColors />
            <RouteTitle />
            <ScrollToTop />
            <AppRoutes />
          </CartProvider>
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
